import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * Owner Notification Center hook.
 *
 * Poll interval: 60 s (cheap `/unread_count` endpoint). The full grouped list
 * is fetched lazily on panel open and after any state-mutating action.
 *
 * Returns:
 *   unreadTotal            : number
 *   unreadByCategory       : Record<category, number>
 *   groups                 : Record<category, Notification[]>
 *   categories             : string[]
 *   loading                : boolean
 *   error                  : string | null
 *   refreshCount()         : re-poll the badge only (cheap)
 *   refreshGroups()        : re-fetch the grouped list (heavier)
 *   markRead(id)           : mark one as read
 *   markAllRead(category?) : mark all (optionally within a category)
 *   dismiss(id)            : delete a notification
 */
export function useOwnerNotifications({ pollMs = 60000 } = {}) {
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [unreadByCategory, setUnreadByCategory] = useState({});
  const [groups, setGroups] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    // StrictMode double-mounts effects in dev: re-arm the flag on each mount.
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get("/owner/notifications/unread_count");
      if (!mountedRef.current) return;
      setUnreadTotal(Number(data?.total || 0));
      setUnreadByCategory(data?.by_category || {});
    } catch (e) {
      // silent — badge just stays at last-known value.
      if (!mountedRef.current) return;
      setError(e?.response?.data?.detail || e?.message || "count failed");
    }
  }, []);

  const refreshGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/owner/notifications");
      if (!mountedRef.current) return;
      setGroups(data?.groups || {});
      setUnreadByCategory(data?.unread_by_category || {});
      setUnreadTotal(Number(data?.unread_total || 0));
      setCategories(Array.isArray(data?.categories) ? data.categories : []);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e?.response?.data?.detail || e?.message || "load failed");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const markRead = useCallback(async (id) => {
    await api.post(`/owner/notifications/${id}/read`);
    await Promise.all([refreshCount(), refreshGroups()]);
  }, [refreshCount, refreshGroups]);

  const markAllRead = useCallback(async (category = null) => {
    await api.post("/owner/notifications/read_all", category ? { category } : {});
    await Promise.all([refreshCount(), refreshGroups()]);
  }, [refreshCount, refreshGroups]);

  const dismiss = useCallback(async (id) => {
    await api.delete(`/owner/notifications/${id}`);
    await Promise.all([refreshCount(), refreshGroups()]);
  }, [refreshCount, refreshGroups]);

  // Initial + polling badge refresh.
  useEffect(() => {
    refreshCount();
    if (!pollMs) return undefined;
    const t = setInterval(refreshCount, pollMs);
    return () => clearInterval(t);
  }, [pollMs, refreshCount]);

  return {
    unreadTotal, unreadByCategory, groups, categories,
    loading, error,
    refreshCount, refreshGroups,
    markRead, markAllRead, dismiss,
  };
}
